import { type ReactNode, type SubmitEvent, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { Alert } from "@/components/ui/Alert/Alert";
import { Button } from "@/components/ui/Button/Button";
import { ChoiceList } from "@/components/ui/CheckboxAndRadio/ChoiceList";
import { Form } from "@/components/ui/Form/Form";
import { FormLayout } from "@/components/ui/Form/FormLayout";
import { t } from "@/i18n/translate";
import { committeeCategoryValues } from "@/types/generated/openapi";
import { StringFormData } from "@/utils/stringFormData";
import { isOneOf } from "@/utils/typeChecks";
import { useElectionCreateContext } from "../hooks/useElectionCreateContext";

export function SelectCommitteeCategory() {
  const { state, dispatch } = useElectionCreateContext();
  const navigate = useNavigate();
  const [error] = useState<ReactNode | undefined>();

  // if no election data was stored, navigate back to beginning
  if (!state.election) {
    return <Navigate to="/elections/create" />;
  }

  // PS CSB is not supported yet
  const csbDisabled = state.election.category === "Provincial";

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new StringFormData(event.currentTarget);
    const committeeCategory = formData.getString("committee_category");
    if (!isOneOf(committeeCategoryValues, committeeCategory) || (committeeCategory === "CSB" && csbDisabled)) {
      return;
    }

    dispatch({
      type: "SET_COMMITTEE_CATEGORY",
      committeeCategory,
    });
    if (committeeCategory === "CSB" || state.election?.category === "Municipal") {
      // Go to upload candidate lists directly
      await navigate("/elections/create/list-of-candidates");
    } else {
      // PS/WS elections need a GSB to be selected first
      await navigate("/elections/create/select-gsb");
    }
  }

  return (
    <section className="md">
      <Form title={t("election.committee_category.title")} onSubmit={(e) => void handleSubmit(e)}>
        <FormLayout>
          <FormLayout.Section>
            {error && (
              <Alert type="error" title={t("election.invalid_committee_category")} inline>
                <p>{error}</p>
              </Alert>
            )}

            <p>{t("election.committee_category.description")}</p>

            <ChoiceList>
              <ChoiceList.Legend>{t("election.committee_category.choose")}</ChoiceList.Legend>
              <ChoiceList.Radio
                id="gsb"
                name={"committee_category"}
                label={t("committee_category.GSB.full")}
                defaultValue={"GSB"}
                defaultChecked={state.committeeCategory === "GSB" || !state.committeeCategory}
              ></ChoiceList.Radio>
              <ChoiceList.Radio
                id="csb"
                name={"committee_category"}
                label={t("committee_category.CSB.full")}
                defaultValue={"CSB"}
                defaultChecked={state.committeeCategory === "CSB" && !csbDisabled}
                disabled={csbDisabled}
              ></ChoiceList.Radio>
            </ChoiceList>
          </FormLayout.Section>

          <FormLayout.Controls>
            <Button type="submit">{t("next")}</Button>
          </FormLayout.Controls>
        </FormLayout>
      </Form>
    </section>
  );
}
